import React, { useState } from 'react';
import { ShoppingCart, Search, Menu, X, Settings, ArrowRight } from 'lucide-react';

const NAV_ITEMS = [
  { id: 'home', label: 'Home' },
  { id: 'products', label: 'Shop' },
  { id: 'about', label: 'About' },
  { id: 'testimonials', label: 'Stories' },
  { id: 'contact', label: 'Contact' },
];

/**
 * Sticky storefront header: logo, primary nav, search, cart, admin entry.
 * Mobile menu + mobile search are self-contained UI state (not shared elsewhere).
 */
const Header = ({
  activeSection,
  scrollToSection,
  searchQuery,
  onSearchChange,
  cartCount,
  onOpenCart,
  onAdminLogin,
}) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);

  return (
    <>
      <header className="bg-paper/95 backdrop-blur-md sticky top-0 z-50 border-b border-line">
        <div className="w-full px-4 sm:px-6 md:px-10 lg:px-14">
          <div className="flex items-center justify-between h-16 sm:h-[4.5rem] md:h-20 w-full">
            {/* Logotype */}
            <button
              onClick={() => scrollToSection('home')}
              className="flex items-baseline gap-1 flex-shrink-0"
            >
              <span className="font-display text-2xl sm:text-3xl text-ink tracking-tight">DPC</span>
              <span className="dpc-label text-ink-soft hidden sm:inline">Collection</span>
            </button>

            {/* Primary nav */}
            <nav className="hidden lg:flex items-center justify-center flex-1 mx-8 xl:mx-14">
              <div className="flex items-center gap-8 xl:gap-12">
                {NAV_ITEMS.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => scrollToSection(item.id)}
                    className={`dpc-label transition-colors duration-200 whitespace-nowrap ${
                      activeSection === item.id
                        ? 'text-accent'
                        : 'text-ink-soft hover:text-ink'
                    }`}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            </nav>

            {/* Actions */}
            <div className="flex items-center gap-1 sm:gap-2 md:gap-3 flex-shrink-0">
              <div className="hidden sm:flex items-center">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-faint w-4 h-4" />
                  <input
                    type="text"
                    placeholder="Search the collection"
                    value={searchQuery}
                    onChange={(e) => onSearchChange(e.target.value)}
                    className="pl-10 pr-4 py-2 text-sm border border-line rounded-full bg-surface focus:ring-1 focus:ring-accent focus:border-accent transition-all duration-200 w-44 lg:w-60 xl:w-72 placeholder:text-ink-faint"
                  />
                </div>
              </div>

              <button
                onClick={() => setMobileSearchOpen(true)}
                className="p-2 text-ink-soft hover:text-ink transition-colors duration-200 sm:hidden"
                aria-label="Search"
              >
                <Search className="w-5 h-5" />
              </button>

              <button
                onClick={onOpenCart}
                className="p-2 text-ink-soft hover:text-accent transition-colors duration-200 relative"
                aria-label="Cart"
              >
                <ShoppingCart className="w-5 h-5" />
                {cartCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 bg-accent text-white text-[10px] rounded-full w-4.5 h-4.5 min-w-[18px] h-[18px] flex items-center justify-center font-semibold">
                    {cartCount}
                  </span>
                )}
              </button>

              <button
                onClick={() => scrollToSection('products')}
                className="hidden sm:flex items-center gap-2 px-5 py-2 bg-ink text-paper dpc-label rounded-full hover:bg-accent transition-colors duration-200"
              >
                Shop now
                <ArrowRight className="w-3.5 h-3.5" />
              </button>

              <button
                onClick={onAdminLogin}
                className="p-2 text-ink-faint hover:text-ink transition-colors duration-200"
                title="Admin"
                aria-label="Admin"
              >
                <Settings className="w-5 h-5" />
              </button>

              <button
                onClick={() => setMobileMenuOpen(true)}
                className="p-2 text-ink-soft hover:text-ink transition-colors duration-200 lg:hidden"
                aria-label="Menu"
              >
                <Menu className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {mobileSearchOpen && (
          <div className="sm:hidden border-t border-line p-4 bg-paper">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-faint w-4 h-4" />
              <input
                type="text"
                placeholder="Search the collection"
                value={searchQuery}
                onChange={(e) => onSearchChange(e.target.value)}
                className="w-full pl-10 pr-12 py-3 text-sm border border-line rounded-full bg-surface focus:ring-1 focus:ring-accent focus:border-accent"
                autoFocus
              />
              <button
                onClick={() => setMobileSearchOpen(false)}
                className="absolute right-1 top-1/2 -translate-y-1/2 p-2 text-ink-faint hover:text-ink"
                aria-label="Close search"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </header>

      {mobileMenuOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="fixed inset-0 bg-ink/50 backdrop-blur-sm" onClick={() => setMobileMenuOpen(false)} />
          <div className="fixed top-0 right-0 w-full sm:w-96 h-full bg-paper shadow-2xl">
            <div className="p-6 sm:p-8">
              <div className="flex items-center justify-between mb-10">
                <span className="font-display text-2xl text-ink">DPC Collection</span>
                <button
                  onClick={() => setMobileMenuOpen(false)}
                  className="p-2.5 hover:bg-surface rounded-full transition-colors"
                  aria-label="Close menu"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              <nav className="flex flex-col gap-6">
                {NAV_ITEMS.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => {
                      scrollToSection(item.id);
                      setMobileMenuOpen(false);
                    }}
                    className="text-left font-display text-3xl text-ink hover:text-accent transition-colors"
                  >
                    {item.label}
                  </button>
                ))}
              </nav>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Header;
